import { useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { RefetchIntervals } from "../../../../../../../core/api/refetchIntervals"
import { useMerchant } from "../../../../../../merchants/merchantHooks"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { useOrderDialogUrlState } from "../../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { CreateOrderDialogMode } from "../../../../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderType } from "../../../../../../../shared/components/createOrder/helpers"
import {
    fetchMeetingServiceOrders,
    MeetingServiceOrdersResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../../meetingServiceApi"
import {
    DeliveryTypeFilter,
    FilteredStatuses,
    LockedFilter,
    MeetingServiceOrder,
    MeetingServiceOrdersView,
    STATUS_FILTER_ALL,
    StatusFilterValue,
    TimePeriodMeetingService,
} from "../../meetingServiceTypes"
import { meetingServiceOrderMatchesSearch } from "../../meetingServiceLib"
import {
    defaultMeetingServiceFilterOptionsList,
    FiltersDrawerMeetingService,
    MeetingServiceFilterProps,
} from "../../../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
// Hidden with the Filter button (Task 260):
// import useActiveFiltersCount from "../../../../../../supplier/reporting/accounting/meetingService/hooks/useActiveFiltersCount"
import {
    groupOrdersByDate,
    groupOrdersByDateAndHour,
    groupOrdersByLocation,
    groupOrdersByStatus,
    ProductionGroupBy,
    ProductionLayout,
} from "../helpers"
import { ProductionFilterChip, ProductionViewToolbar } from "../components/ProductionViewToolbar"
import { ProductionViewHeader } from "../components/ProductionViewHeader"
import { ProductionViewBody } from "../components/ProductionViewBody"
import { ProductionDeleteDialog } from "../components/ProductionDeleteDialog"
import { ProductionBulkActionBar } from "../components/ProductionBulkActionBar"

const PRODUCTION_DISABLED_FILTERS = {
    disableDeliveryDate: true,
    disableInvoicing: true,
    disablePendingApproval: true,
    disableMerchantShops: true,
    disableServicePayment: true,
}

const useStyles = makeStyles()(() => ({
    root: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
    },
    savedDialogActions: {
        display: "flex",
        justifyContent: "center",
        padding: "1rem 0 .5rem",
    },
    fullscreenContent: {
        padding: "1rem 1.5rem 0",
    },
}))

// The API can return an order once per delivery; the production view shows one card per order, so dedupe by id.
const selectOrders = (data: MeetingServiceOrdersResponse): MeetingServiceOrder[] => {
    const seen = new Set<number>()
    return (data?.orders ?? []).filter((order) => {
        if (seen.has(order.id)) return false
        seen.add(order.id)
        return true
    })
}

function ProductionViewContent() {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { merchantId } = useMerchant()
    const { isOpen: isDialogOpen, openDialog } = useOrderDialogUrlState()

    const [selectedDate, setSelectedDate] = useState<DateTime>(() => dateTimeFactory.now().startOf("day"))
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(STATUS_FILTER_ALL)
    const [filtersList, setFiltersList] = useState<MeetingServiceFilterProps>(() => ({
        ...defaultMeetingServiceFilterOptionsList,
        merchantId: merchantId.toString(),
    }))
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [groupBy, setGroupBy] = useState<ProductionGroupBy>(ProductionGroupBy.TIME)
    const [search, setSearch] = useState("")
    const [ordersToDelete, setOrdersToDelete] = useState<MeetingServiceOrder[]>([])
    const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([])
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showSavedDialog, setShowSavedDialog] = useState(false)
    // "Not approved" tab: switches the query to the NOT_APPROVED view to list only orders awaiting approval.
    const [notApprovedView, setNotApprovedView] = useState(false)

    // Hidden with the Filter button (Task 260) — restore for the filter badge count:
    // const { activeCount } = useActiveFiltersCount(filtersList, PRODUCTION_DISABLED_FILTERS)

    const filterChips = useMemo<ProductionFilterChip[]>(() => {
        const chips: ProductionFilterChip[] = []
        const {
            deliveryType,
            isLocked,
            orderId,
            customerId,
            email,
            employeeId,
            merchantIds,
            satelliteIds,
            buyerPartyIds,
        } = filtersList

        const resetField = (key: keyof MeetingServiceFilterProps) =>
            setFiltersList((prev) => ({ ...prev, [key]: defaultMeetingServiceFilterOptionsList[key] }))
        const removeFromArray = (key: "merchantIds" | "satelliteIds" | "buyerPartyIds", id: string) =>
            setFiltersList((prev) => ({ ...prev, [key]: (prev[key] ?? []).filter((value) => value !== id) }))

        if (deliveryType && deliveryType !== DeliveryTypeFilter.ALL) {
            chips.push({
                key: "deliveryType",
                label: l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType),
                onRemove: () => resetField("deliveryType"),
            })
        }
        if (isLocked && isLocked !== LockedFilter.ALL) {
            chips.push({
                key: "isLocked",
                label: l10n.getStringForEnum("meeting-service-production-filter-locked", isLocked, isLocked),
                onRemove: () => resetField("isLocked"),
            })
        }
        if (orderId) chips.push({ key: "orderId", label: orderId, onRemove: () => resetField("orderId") })
        if (customerId) chips.push({ key: "customerId", label: customerId, onRemove: () => resetField("customerId") })
        if (email) chips.push({ key: "email", label: email, onRemove: () => resetField("email") })
        if (employeeId) chips.push({ key: "employeeId", label: employeeId, onRemove: () => resetField("employeeId") })
        merchantIds?.forEach((id) =>
            chips.push({ key: `merchant-${id}`, label: id, onRemove: () => removeFromArray("merchantIds", id) }),
        )
        satelliteIds?.forEach((id) =>
            chips.push({ key: `satellite-${id}`, label: id, onRemove: () => removeFromArray("satelliteIds", id) }),
        )
        buyerPartyIds?.forEach((id) =>
            chips.push({ key: `buyer-${id}`, label: id, onRemove: () => removeFromArray("buyerPartyIds", id) }),
        )
        return chips
    }, [filtersList, l10n, setFiltersList])

    const selectedDateIso = selectedDate.toISODate() ?? undefined

    const dependencies = useMemo(() => {
        const {
            startBookedDate: _startBookedDate,
            endBookedDate: _endBookedDate,
            startDeliveryDate: _startDeliveryDate,
            endDeliveryDate: _endDeliveryDate,
            pendingApproval: _pendingApproval,
            invoiceStatus: _invoiceStatus,
            isBooked: _isBooked,
            isLocked: _isLocked,
            ...restFilters
        } = filtersList

        return {
            ...restFilters,
            merchantId,
            view: notApprovedView ? MeetingServiceOrdersView.NOT_APPROVED : MeetingServiceOrdersView.PRODUCTION,
            period: TimePeriodMeetingService.ALL,
            startDeliveryDate: selectedDateIso,
            endDeliveryDate: selectedDateIso,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        }
    }, [filtersList, merchantId, selectedDateIso, notApprovedView])

    const ordersResult = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies,
        select: selectOrders,
        refetchInterval: RefetchIntervals.medium,
    })

    // Count of not-approved orders for the selected date
    const notApprovedDependencies = useMemo(
        () => ({ ...dependencies, view: MeetingServiceOrdersView.NOT_APPROVED }),
        [dependencies],
    )
    const notApprovedCountResult = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: notApprovedDependencies,
        select: (data: MeetingServiceOrdersResponse) => selectOrders(data).length,
        refetchInterval: RefetchIntervals.medium,
    })
    const notApprovedCount = notApprovedCountResult.data ?? 0

    // Refetch when the createOrder edit dialog closes, so the list reflects edits.
    const prevDialogOpen = useRef(isDialogOpen)
    useEffect(() => {
        if (prevDialogOpen.current && !isDialogOpen) {
            ordersResult.refetch()
        }
        prevDialogOpen.current = isDialogOpen
    }, [isDialogOpen, ordersResult])

    const layout = useMemo<ProductionLayout>(() => {
        const lockedFilter = filtersList.isLocked
        const filtered = (ordersResult.data ?? [])
            .filter((order) => meetingServiceOrderMatchesSearch(order, search))
            .filter(
                (order) =>
                    statusFilter === STATUS_FILTER_ALL || order.deliveryDetails.statusDetails.status === statusFilter,
            )
            .filter((order) => {
                if (!lockedFilter || lockedFilter === LockedFilter.ALL) return true
                const isLocked = Boolean(order.deliveryDetails.isLocked)
                return lockedFilter === LockedFilter.LOCKED ? isLocked : !isLocked
            })
        // "Not approved" spans many delivery dates (the date filter is ignored for that view), so group by
        // delivery date to surface it — the grouping tabs don't apply here.
        if (notApprovedView) {
            return { mode: ProductionGroupBy.DATE, dateGroups: groupOrdersByDate(filtered, dateTimeFactory) }
        }
        if (groupBy === ProductionGroupBy.STATUS) {
            return {
                mode: ProductionGroupBy.STATUS,
                statusGroups: groupOrdersByStatus(filtered).map(([status, orders]) => ({ status, orders })),
            }
        }
        if (groupBy === ProductionGroupBy.LOCATION) {
            return {
                mode: ProductionGroupBy.LOCATION,
                locationSections: groupOrdersByLocation(filtered, dateTimeFactory),
            }
        }
        return { mode: ProductionGroupBy.TIME, sections: groupOrdersByDateAndHour(filtered, dateTimeFactory) }
    }, [ordersResult.data, search, statusFilter, filtersList.isLocked, groupBy, notApprovedView, dateTimeFactory])

    const isEmpty = useMemo(() => {
        if (ordersResult.isInitialLoading) return false
        if (layout.mode === ProductionGroupBy.STATUS) return layout.statusGroups.length === 0
        if (layout.mode === ProductionGroupBy.LOCATION) return layout.locationSections.length === 0
        if (layout.mode === ProductionGroupBy.DATE) return layout.dateGroups.length === 0
        return layout.sections.length === 0
    }, [ordersResult.isInitialLoading, layout])

    const handleOpenEdit = (orderId: number) =>
        openDialog({
            orderId,
            mode: CreateOrderDialogMode.EDIT,
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })

    const selectedOrders = useMemo(() => {
        const selectedIds = new Set(selectedOrderIds)
        return (ordersResult.data ?? []).filter((order) => selectedIds.has(order.id))
    }, [ordersResult.data, selectedOrderIds])

    // Hidden for now (Task 260) — stat cards data (was passed to ProductionViewHeader as `orderStats`):
    // const orderStats = useMemo(() => {
    //     const orders = ordersResult.data ?? []
    //     const delivered = orders.filter((order) => isDeliveredStatus(order.deliveryDetails.statusDetails.status)).length
    //     return { total: orders.length, delivered }
    // }, [ordersResult.data])

    // Only offer the statuses actually present in the loaded orders
    const availableStatuses = useMemo<FilteredStatuses[]>(() => {
        const seen = new Set<FilteredStatuses>()
        for (const order of ordersResult.data ?? []) {
            seen.add(order.deliveryDetails.statusDetails.status)
        }
        return Array.from(seen)
    }, [ordersResult.data])

    useEffect(() => {
        if (statusFilter !== STATUS_FILTER_ALL && !availableStatuses.includes(statusFilter)) {
            setStatusFilter(STATUS_FILTER_ALL)
        }
    }, [availableStatuses, statusFilter])

    const toggleSelect = (orderId: number) =>
        setSelectedOrderIds((prev) =>
            prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId],
        )

    const clearSelection = () => setSelectedOrderIds([])

    const content = (
        <Box className={cx(classes.root, isFullscreen && classes.fullscreenContent)}>
            {/* Hidden for now (Task 260): also pass orderStats={orderStats} to restore the stat cards. */}
            <ProductionViewHeader
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                isFullscreen={isFullscreen}
                notApprovedView={notApprovedView}
                notApprovedCount={notApprovedCount}
                onNotApprovedChange={setNotApprovedView}
                onEnterFullscreen={() => setIsFullscreen(true)}
                onExitFullscreen={() => setIsFullscreen(false)}
            >
                <ProductionViewToolbar
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    onOpenFilters={() => setDrawerOpen(true)}
                    search={search}
                    onSearchChange={setSearch}
                    filterChips={filterChips}
                    hideDatePicker={notApprovedView}
                />
            </ProductionViewHeader>

            <FiltersDrawerMeetingService
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
                setFiltersList={setFiltersList}
                disabledFilters={PRODUCTION_DISABLED_FILTERS}
                enableProductionFilters
                raiseAboveDialog
            />

            <ProductionViewBody
                isInitialLoading={ordersResult.isInitialLoading}
                isEmpty={isEmpty}
                layout={layout}
                selectedOrderIds={selectedOrderIds}
                onToggleSelect={toggleSelect}
                onEditOrder={handleOpenEdit}
                onDeleteOrder={(order) => setOrdersToDelete([order])}
                onOrderSaved={() => setShowSavedDialog(true)}
                meetingServiceDependencies={dependencies}
            />

            {showSavedDialog && (
                <StandardDialog
                    open
                    onClose={() => setShowSavedDialog(false)}
                    maxWidthProp="xs"
                    dialogTitle={l10n.getString("meeting-service-production-view-saved-dialog-title")}
                    subtitle={l10n.getString("meeting-service-production-view-saved-dialog-message")}
                >
                    <Box className={classes.savedDialogActions}>
                        <LocalizedStrict id="meeting-service-production-view-saved-dialog-close">
                            <StandardButton
                                fullWidth={false}
                                variant="contained"
                                onClick={() => setShowSavedDialog(false)}
                            >
                                OK
                            </StandardButton>
                        </LocalizedStrict>
                    </Box>
                </StandardDialog>
            )}

            {ordersToDelete.length > 0 && (
                <ProductionDeleteDialog
                    orders={ordersToDelete}
                    onClose={() => setOrdersToDelete([])}
                    onDeleted={() => {
                        setOrdersToDelete([])
                        clearSelection()
                    }}
                />
            )}

            <ProductionBulkActionBar
                selectedOrders={selectedOrders}
                selectedOrderIds={selectedOrderIds}
                merchantId={merchantId}
                onClearSelection={clearSelection}
                onRequestDelete={(orders) => setOrdersToDelete(orders)}
            />
        </Box>
    )

    if (!isFullscreen) return content

    return (
        <StandardDialog open isFullScreen onClose={() => setIsFullscreen(false)} topbar={<></>}>
            {content}
        </StandardDialog>
    )
}

export function MeetingServiceProductionView() {
    return <ProductionViewContent />
}
